import React from "react";

// CLASSES
import Validator from "@classes/Validator";

// COMPONENTS
import ActivityIndicator from "@components/ActivityIndicator/ActivityIndicator";
import Button from "@components/Button/Button";
import Checkbox from "@components/Checkbox/Checkbox";
import ContentBox from "@components/ContentBox/ContentBox";
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import Modal from "@components/Modal/Modal";
import Select from "@components/Select/Select";
import StackLayout from "@components/layouts/StackLayout/StackLayout";
import TextField from "@components/TextField/TextField";

// HELPERS
import {createClassName} from "@helpers/utils";
import i18n from "@helpers/i18n";
import countries from "@helpers/countries";

// OTHER
import fulfillment from "./CustomerInformation.fulfillment";
import OptionsList from "../../../components/OptionsList/OptionsList";
import LeiCodeField from "../../../components/LeiCode/LeiCodeField";
import LeiCodeFieldFulfillment from "../../../components/LeiCode/LeiCodeField.fulfillment";
import TextView from "../../../../../components/TextView/TextView";
import PartnerHiddenFields from "../../../components/PartnerHiddenFields/PartnerHiddenFields";

export default class CustomerInformation extends React.PureComponent
{
	state = _getInitialState.call(this);
	fields = _getFields.call(this);
	countries = countries.map(({label, code}) => ({label, value: label}));

	isFulfilled(state)
	{
		return fulfillment(state, this.state);
	}

	setCustomerState(customerState, callbackFn)
	{
		customerState = {...this.state.customerState, ...customerState};
		this.setState({
			customerState,
			isFulfilled: this.isFulfilled({customerState})
		}, () =>
		{
			this.props.setAppState({viewFulfilled: this.state.isFulfilled});
			if (callbackFn)
			{
				callbackFn();
			}
		});
	}

	setLookupInfo(lookupInfo)
	{
		const {props, state} = this;
		const {customerState} = state;
		const {isCompany} = props.customer;

		const clientInfoPhone = lookupInfo.client.phone && lookupInfo.client.phone !== "-" ? lookupInfo.client.phone : "";
		const clientInfoPhoneAlt = lookupInfo.client.phone_alternative && lookupInfo.client.phone_alternative !== "-" ? lookupInfo.client.phone_alternative : "";

		this.setState({lookupInfo: lookupInfo}, () =>
		{
			this.setCustomerState({
				firstName: customerState.firstName || lookupInfo[isCompany ? "companyName" : "firstName"] || "",
				lastName: isCompany ? "" : (customerState.lastName || lookupInfo.lastName || ""),
				street: customerState.street || lookupInfo.street || "",
				zipCode: customerState.zipCode || lookupInfo.zipCode || "",
				city: customerState.city || lookupInfo.city || "",
				email: customerState.email || lookupInfo.email || lookupInfo.client.email || "",
				phoneNumber: customerState.phoneNumber || lookupInfo.phoneNumber || clientInfoPhone || "",
				phoneNumberHome: customerState.phoneNumberHome || lookupInfo.phoneNumberHome || clientInfoPhoneAlt || "",
				leiCodeValid: LeiCodeFieldFulfillment.call(this, LeiCodeField.getViewInitialStateForCustomer(props.customer, lookupInfo, false), null)
			});
		});
	}

	async componentDidMount()
	{
		const {props} = this;
		const {customer, setAppState} = props;
		const ssnOrg = customer.getData("customer_social_security_number", null);

		// Set navigation events
		setAppState({
			viewFulfilled: this.isFulfilled(),
			showValidationWarning: false,
			navigationButtons: {
				sharing: {},
				back: {},
				next: {
					onClick: () =>
					{
						if (this.state.isFulfilled === true)
						{
							return;
						}

						this.setState({hasFormValidationErrors: true});
						return setAppState({
							showValidationWarning: true
						});
					}
				}
			}
		});

		if (!!ssnOrg)
		{
			if (!customer.lookupInfo ||
				customer.lookupInfo.name === undefined ||
				customer.lookupInfo.name.length === 0)
			{
				try
				{
					await customer.lookupAsync();
					const lookupNotFound = customer.lookupInfo.type === undefined;
					this.setState({
						isLoading: false,
						lookupInfo: null,
						lookupNotFound
					}, () => this.setLookupInfo(customer.lookupInfo));
				}
				catch (e)
				{
					if (e.message === 'FRAUD_FLAG')
					{
						console.warn("Customer information locked. Case can not be completed.");
						this.setState({isLoading: false, lookupInfo: null, lookupNotFound: true, fraudFlag: true});
					}
					else
					{
						console.warn("Customer information not found. Information must be entered manually.");
						this.setState({isLoading: false, lookupInfo: null, lookupNotFound: true, fraudFlag: false});
					}
				}
			}
			else
			{
				this.setState({isLoading: false, lookupInfo: null}, () => this.setLookupInfo(customer.lookupInfo));
			}
		}
		else
		{
			this.setState({isLoading: false, lookupInfo: null, lookupNotFound: true});
		}
	}

	render()
	{
		const {props, state} = this;
		const {user, customer, onRestartSession} = props;
		const {customerState, lookupNotFound, readOnly} = state;
		const classes = createClassName("Cob-view Cob-view2", {});

		const hasFormValidationErrors = !this.state.isFulfilled && this.state.hasFormValidationErrors;

		if (state.isLoading)
		{
			return <ActivityIndicator busy/>;
		}

		const companySignatoriesForDisplay = [];

		if (customer.isCompany)
		{
			for (let i = 0; i < customerState.companySignatories; i++)
			{
				const fieldNameIndex = i + 1;
				const fieldNameSuffix = fieldNameIndex === 1 ? "" : ("_" + fieldNameIndex);

				companySignatoriesForDisplay.push(
					<div className="Cob-view2-CompanyReps-Rep" style={{paddingBottom: "0"}} key={fieldNameIndex}>
						<h4 style={{marginBottom: "5px"}}>Fullmaktstagare/firmatecknare {fieldNameIndex}</h4>
						<StackLayout className="Cob-fields-collection per-row-3" rowWrap fullWidth>
							<div>
								<TextField
									name={`customer_inv_poa_firstname${fieldNameSuffix}`}
									label="Förnamn"
									autocomplete="off"
									value={customerState[`customer_inv_poa_firstname${fieldNameSuffix}`]}
									validation={this._textValidation}
									onChange={({target}) => this.setCustomerState({[`customer_inv_poa_firstname${fieldNameSuffix}`]: target.value})}
									highlightErrors={hasFormValidationErrors}
                                    readOnly={readOnly}
								/>
							</div>
							<div>
								<TextField
									name={`customer_inv_poa_lastname${fieldNameSuffix}`}
									label="Efternamn"
									autocomplete="off"
									value={customerState[`customer_inv_poa_lastname${fieldNameSuffix}`]}
									validation={this._textValidation}
									onChange={({target}) => this.setCustomerState({[`customer_inv_poa_lastname${fieldNameSuffix}`]: target.value})}
									highlightErrors={hasFormValidationErrors}
                                    readOnly={readOnly}
								/>
							</div>
							<div>
								<TextField
									name={`customer_inv_poa_ssn${fieldNameSuffix}`}
									label="Personnummer"
									autocomplete="off"
									value={customerState[`customer_inv_poa_ssn${fieldNameSuffix}`]}
									validation={this._ssnValidation}
									onChange={({target}) => this.setCustomerState({[`customer_inv_poa_ssn${fieldNameSuffix}`]: target.value})}
									highlightErrors={hasFormValidationErrors}
                                    readOnly={readOnly}
								/>
							</div>
						</StackLayout>
					</div>
				);
			}
		}

		return (
			<div className={classes}>
				<DefaultFields fields={this.fields}/>
				<PartnerHiddenFields customer={customer} user={user} />
				<input type="hidden" name="customer_phonenumber_home" value={customerState.phoneNumberHome || "-"}/>
				<input type="hidden" name="customer_phonenumber_cell" value={customerState.phoneNumber || "-"}/>
				<input type="hidden" name="radio_customer_type" value={customer.isCompany ? "company" : "person"}/>

				<Modal visible={state.fraudFlag} status="danger" title="Personnummer låst">
					<React.Fragment>
						<p>
							Kundens personnummer är låst via en extern ID-spärr.
							Vänligen kontakta Fair Investments.
						</p>

						<Modal.ButtonsContainer>
							<Modal.Button label="Avsluta rådgivning" appearance="primary"
										  onClick={onRestartSession} filled/>
						</Modal.ButtonsContainer>
					</React.Fragment>
				</Modal>

				<ContentBox>
					<h2>Kundinformation</h2>

					<div className="Cob-view2-fields">

						<h3>Grunduppgifter</h3>

						<StackLayout className="Cob-fields-collection per-row-3" rowWrap fullWidth>
							<div>
								<TextField
									name="date"
									value={customerState.date}
									autocomplete="off"
									label="Datum för rådgivningen"
									validation={value => value.length > 0}
									onChange={({target}) => this.setCustomerState({date: target.value})}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
							</div>
							<div>
								<Select
									label="Rådgivningens utförande"
									selectedIndex={customerState.meetingFormatIndex}
									options={[
										{label: "Personligt möte", value: "personal"},
										{label: "Telefonmöte", value: "phone"},
										{label: "Videomöte", value: "video"}
									]}
									onChange={(option, meetingFormatIndex) => this.setCustomerState({
										meetingFormat: option.value,
										meetingFormatIndex
									})}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>

								<input type={"hidden"} name="radio_meeting_type"
								       value={customerState.meetingFormat == null ? "" : customerState.meetingFormat}/>
							</div>
						</StackLayout>

						{(customerState.meetingFormat === "phone" || customerState.meetingFormat === "video") && (
							<React.Fragment>
								<strong>Vid rådgivning på distans (samtliga ska uppfyllas)</strong>

								<div style={{flexDirection: "row", marginBottom: "30px", marginTop: "5px"}}>
									<Checkbox
										name={"checkbox_customer_extcheck"}
										label={"Kontroll mot externa register"}
										checked={customerState.checkbox_customer_extcheck === "yes"}
										value={"yes"}
										highlightErrors={hasFormValidationErrors}
										readOnly={readOnly}
										onChange={checked => this.setCustomerState({checkbox_customer_extcheck: checked ? "yes" : "no"})}
									/>
									<Checkbox
										name={"checkbox_customer_countercheck"}
										label={"Utfört motkontroll"}
										checked={customerState.checkbox_customer_countercheck === "yes"}
										value={"yes"}
										highlightErrors={hasFormValidationErrors}
										readOnly={readOnly}
										onChange={checked => this.setCustomerState({checkbox_customer_countercheck: checked ? "yes" : "no"})}
										style={{marginLeft: "40px"}}
									/>
								</div>
							</React.Fragment>
						)}

						{(customerState.meetingFormat !== "phone" && customerState.meetingFormat !== "video") && (
							<React.Fragment>
								<input type={"hidden"} name={"checkbox_customer_extcheck"} value={""} />
								<input type={"hidden"} name={"checkbox_customer_countercheck"} value={""} />
							</React.Fragment>
						)}

						<h3 style={{paddingBottom: "10px"}}>Identitetskontroll</h3>
						<OptionsList
							type="checkbox"
							description="Rådgivaren har tagit del av"
							orientation="horizontal"
							style={{marginBottom: "20px"}}
							value={
								customerState.checkbox_idcheck_id === "yes" ||
								customerState.checkbox_idcheck_drivinglic === "yes" ||
								customerState.checkbox_idcheck_passport === "yes" ||
								customerState.checkbox_idcheck_known === "Ja" ?
									"filled" :
									""
							}
							options={[
								{
									name: "checkbox_idcheck_id",
									label: "ID-kort",
									value: customerState.checkbox_idcheck_id === "yes" ? "yes" : "no",
									checked: customerState.checkbox_idcheck_id === "yes",
									onChange: checked => this.setCustomerState({checkbox_idcheck_id: checked ? "yes" : "no"})
								},
								{
									name: "checkbox_idcheck_drivinglic",
									label: "Körkort",
									value: customerState.checkbox_idcheck_drivinglic === "yes" ? "yes" : "no",
									checked: customerState.checkbox_idcheck_drivinglic === "yes",
									onChange: checked => this.setCustomerState({checkbox_idcheck_drivinglic: checked ? "yes" : "no"})
								},
								{
									name: "checkbox_idcheck_passport",
									label: "Pass",
									value: customerState.checkbox_idcheck_passport === "yes" ? "yes" : "no",
									checked: customerState.checkbox_idcheck_passport === "yes",
									onChange: checked => this.setCustomerState({checkbox_idcheck_passport: checked ? "yes" : "no"})
								},
								{
									name: "checkbox_idcheck_known",
									label: "Känd",
									value: customerState.checkbox_idcheck_known === "Ja" ? "Ja" : "no",
									checked: customerState.checkbox_idcheck_known === "Ja",
									onChange: checked => this.setCustomerState({checkbox_idcheck_known: checked ? "Ja" : "no"})
								}
							]}
							validation={value => (value && value === "filled") || false}
							highlightErrors={hasFormValidationErrors}
							readOnly={readOnly}
						/>

						<div className="h-line spacing"/>

						{lookupNotFound && !customer.isCompany && (
							<React.Fragment>
								<StackLayout orientation="vertical" alignItems="start">
									<Checkbox
										name="checkbox_customer_foreigner"
										label={i18n("cob", "lives_overseas_foreign_citizen")}
										checked={customerState.isForeigner}
										onChange={isForeigner => this.setCustomerState({isForeigner})}
                                        readOnly={readOnly}
									/>
									{customerState.isForeigner && (
										<Checkbox
											style={{marginTop: 10}}
											label="Är bosatt i EU"
											checked={customerState.isNonEuPerson}
											onChange={
												isNonEuPerson => this.setState({
													errorMessage: "För hantering av personer bosatta utanför EU, var god kontakta backoffice."
												}, () => this.setCustomerState({isNonEuPerson}))
											}
                                            readOnly={readOnly}
										/>
									)}
								</StackLayout>
							</React.Fragment>
						)}

						<StackLayout className="Cob-fields-collection per-row-3" rowWrap fullWidth>
							<div style={{alignSelf: "flex-start"}}>
								<TextField
									name="customer_firstname"
									value={customerState.firstName}
									autocomplete="off"
									label={i18n("general", customer.isCompany ? "organization_name" : "first_name")}
									validation={lookupNotFound ? value => value.length > 0 : undefined}
									onChange={lookupNotFound ? ({target}) => this.setCustomerState({firstName: target.value}) : undefined}
									highlightErrors={hasFormValidationErrors}
									readOnly={!lookupNotFound || readOnly}
								/>
							</div>

							{!customer.isCompany && (
								<div style={{alignSelf: "flex-start"}}>
									<TextField
										name="customer_lastname"
										value={customerState.lastName}
										autocomplete="off"
										label={i18n("general", "last_name")}
										validation={lookupNotFound ? value => value.length > 0 : undefined}
										onChange={lookupNotFound ? ({target}) => this.setCustomerState({lastName: target.value}) : undefined}
										highlightErrors={hasFormValidationErrors}
										readOnly={!lookupNotFound || readOnly}
									/>
								</div>
							)}

							<div style={{alignSelf: "flex-start"}}>
								<TextField
									name="customer_social_security_number"
									autocomplete="off"
									value={customerState.ssnOrg}
									label={i18n("general", customer.isCompany ? "organization_number" : "social_security_number")}
									validation={lookupNotFound ? value => value.length > 0 && Validator.validateSsn(value) : undefined}
									onChange={({target}) => this.setCustomerState({ssnOrg: target.value})}
									highlightErrors={hasFormValidationErrors}
									readOnly={!lookupNotFound}
								/>
								<input type={"hidden"} name={"customer_social_security_number"}
								       value={(customerState.ssnOrg.length > 0 && Validator.validateSsn(customerState.ssnOrg)) ? customerState.ssnOrg : ""} />
							</div>

							{customer.isCompany && (
								<div style={{alignSelf: "flex-start"}}>
									<LeiCodeField customer={customer}
									              lookupInfo={state.lookupInfo}
									              onUpdated={this._onLeiUpdated}
									              required={false}
									              highlightErrors={hasFormValidationErrors}
									              readOnly={readOnly}
									/>
								</div>
							)}
						</StackLayout>

						<StackLayout className="Cob-fields-collection per-row-3" rowWrap fullWidth>
							<div>
								<TextField
									name="customer_dont_autocomplete_1"
									value={customerState.street}
									label={i18n("general", "street_address")}
									autocomplete="off"
									validation={value => value.length > 0}
									onChange={({target}) => this.setCustomerState({street: target.value})}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
								<input type={"hidden"} name={"customer_address_street"} value={customerState.street} />
							</div>
							<div>
								<TextField
									name="customer_dont_autocomplete_2"
									value={customerState.zipCode}
									label={i18n("general", "zip_code")}
									autocomplete="off"
									validation={value => value.length > 0}
									onChange={({target}) => this.setCustomerState({zipCode: target.value})}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
								<input type={"hidden"} name={"customer_address_zipcode"} value={customerState.zipCode} />
							</div>
							<div>
								<TextField
									name="customer_dont_autocomplete_3"
									value={customerState.city}
									label={i18n("general", "city")}
									autocomplete="off"
									validation={value => value.length > 0}
									onChange={({target}) => this.setCustomerState({city: target.value})}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
								<input type={"hidden"} name={"customer_address_city"} value={customerState.city} />
							</div>
							{!customer.isCompany && (
								<React.Fragment>
									<div>
										<Select
											name="customer_address_country"
											label={i18n("general", "country")}
											selectedIndex={customerState.countryIndex}
											options={this.countries}
											onChange={({value: country}, countryIndex) => this.setCustomerState({
												country,
												countryIndex
											})}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>
									</div>
								</React.Fragment>
							)}
							<div>
								<TextField
									name="customer_dont_autocomplete_4"
									value={customerState.phoneNumberHome === "-" ? "" : customerState.phoneNumberHome}
									label={"Hemtelefon"}
									autocomplete="off"
									validation={customerState.phoneNumberHome && customerState.phoneNumberHome !== "-" ? Validator.validatePhoneNum : false}
									placeholder={"+46 XX XXXXXX"}
									onChange={({target}) => this.setCustomerState({phoneNumberHome: target.value === "" ? "-" : target.value})}
									readOnly={readOnly}
								/>
								<input type={"hidden"} name={"customer_phonenumber_home_input"}
								       value={customerState.phoneNumberHome === "-" ? "" : customerState.phoneNumberHome} />
							</div>
							<div>
								<TextField
									name="customer_dont_autocomplete_5"
									value={customerState.phoneNumber === "-" ? "" : customerState.phoneNumber}
									label="Mobilnummer"
									autocomplete="off"
									validation={customerState.phoneNumber && customerState.phoneNumber !== "-" ? Validator.validatePhoneNum : false}
									placeholder={"+46 XX XXXXXX"}
									onChange={({target}) => this.setCustomerState({phoneNumber: target.value === "" ? "-" : target.value})}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
								<input type={"hidden"} name={"customer_phonenumber_cell_input"}
								       value={customerState.phoneNumber === "-" ? "" : customerState.phoneNumber} />
							</div>
							<div>
								<TextField
									type="email"
									name="customer_dont_autocomplete_6"
									value={customerState.email}
									autocomplete="off"
									label={i18n("general", "email_address")}
									validation={Validator.validateEmail}
									onChange={({target}) => this.setCustomerState({email: target.value})}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
								<input type={"hidden"} name={"customer_email"} value={customerState.email} />
							</div>
						</StackLayout>

						{!customer.isCompany && (<React.Fragment>
								<OptionsList
									type="checkbox"
									description="För vems räkning agerar kunden?"
									orientation="horizontal"
									style={{
										marginBottom: "20px"
									}}
									value={customerState.checkbox_customer_kyc_inv_own_part === "yes" || customerState.checkbox_customer_kyc_inv_through_poa === "ja" ? "filled" : ""}
									options={[{
										name: "checkbox_customer_kyc_inv_own_part",
										label: "För egen räkning",
											value: customerState.checkbox_customer_kyc_inv_own_part === "yes" ? "yes" : "no",
											checked: customerState.checkbox_customer_kyc_inv_own_part === "yes",
											onChange: checked => this.setCustomerState({checkbox_customer_kyc_inv_own_part: checked ? "yes" : "no"})
										}, {
										name: "checkbox_customer_kyc_inv_through_poa",
										label: "Själv via fullmaktstagare",
										value: customerState.checkbox_customer_kyc_inv_through_poa === "ja" ? "ja" : "nej",
										checked: customerState.checkbox_customer_kyc_inv_through_poa === "ja",
										onChange: checked => this.setCustomerState({checkbox_customer_kyc_inv_through_poa: checked ? "ja" : "nej"}),
									}]}
									validation={value => (value && value === "filled") || false}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
						</React.Fragment>)}

						{!customer.isCompany && customerState.checkbox_customer_kyc_inv_through_poa === "ja" && (
							<div style={{width: "100%", marginBottom: "20px", alignItems: "flex-start"}}>
								<strong>Uppgifter för fullmaktstagare</strong>

								<StackLayout className="Cob-fields-collection per-row-3" rowWrap fullWidth>
									<div>
										<TextField
											name="customer_dont_autocomplete_7"
											label="Förnamn"
											autocomplete="off"
											value={customerState.customer_inv_poa_firstname}
											validation={this._textValidation}
											onChange={({target}) => this.setCustomerState({customer_inv_poa_firstname: target.value})}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>
										<input type={"hidden"} name={"customer_inv_poa_firstname"} value={customerState.customer_inv_poa_firstname} />
									</div>
									<div>
										<TextField
											name="customer_dont_autocomplete_8"
											label="Efternamn"
											autocomplete="off"
											value={customerState.customer_inv_poa_lastname}
											validation={this._textValidation}
											onChange={({target}) => this.setCustomerState({customer_inv_poa_lastname: target.value})}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>
										<input type={"hidden"} name={"customer_inv_poa_lastname"} value={customerState.customer_inv_poa_lastname} />
									</div>
									<div>
										<TextField
											name="customer_dont_autocomplete_9"
											label="Personnummer"
											autocomplete="off"
											value={customerState.customer_inv_poa_ssn}
											validation={this._ssnValidation}
											onChange={({target}) => this.setCustomerState({customer_inv_poa_ssn: target.value})}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>
										<input type={"hidden"} name={"customer_inv_poa_ssn"} value={customerState.customer_inv_poa_ssn} />
									</div>
								</StackLayout>
							</div>)}

						{customer.isCompany && (
							<div style={{width: "100%", marginBottom: "20px", alignItems: "flex-start"}}>
								<input type="hidden" name="checkbox_customer_kyc_inv_through_poa" value="yes"/>

								<h4>Kund agerar via fullmaktstagare / firmatecknare</h4>

								{companySignatoriesForDisplay}

								<StackLayout fullWidth>
									<Button
										size="small"
										label={i18n("general", "remove_last")}
										appearance={customerState.companySignatories <= 1 ? "medium" : "danger"}
										disabled={readOnly || customerState.companySignatories <= 1}
										onClick={() => this.setCustomerState({companySignatories: customerState.companySignatories - 1})}
									/>

									<Button
										size="small"
										label="Lägg till firmatecknare"
										appearance={customerState.companySignatories >= 4 ? "medium" : "primary"}
										disabled={readOnly || customerState.companySignatories >= 4}
										onClick={() => this.setCustomerState({companySignatories: customerState.companySignatories + 1})}
										filled
									/>
								</StackLayout>
							</div>
						)}

						{customerState.needsGuardian && (
							<React.Fragment>
								<div className="h-line spacing"/>

								<h3>Uppgift om vårdnadshavare</h3>
								<p style={{paddingBottom: "10px"}}>
									Då personen är minderårig måste vårdnadshavare anges.
								</p>

								<OptionsList
									style={{paddingBottom: "0", marginBottom: "0"}}
									type="radio"
									name="child_caretaker"
									value={customerState.child_caretaker}
									options={[
										{
											label: "Ensam vårdnadshavare",
											value: "alone",
											checked: customerState.child_caretaker === "alone"
										},
										{
											label: "Två vårdnadshavare",
											value: "two",
											checked: customerState.child_caretaker === "two"
										}
									]}
									onChange={(checked, child_caretaker) => this.setCustomerState({child_caretaker})}
									validation={this._optionsValidation}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
							</React.Fragment>
						)}

						{customerState.needsGuardian && customerState.child_caretaker === "alone" && (
							<React.Fragment>
								<StackLayout className="Cob-fields-collection" alignItems="start" fullWidth>
									<div>
										<h4>Vårdnadshavare</h4>

										<TextField
											name="caretaker_name_1"
											label="Namn"
											autocomplete="off"
											value={customerState.caretaker_name_1}
											validation={this._textValidation}
											onChange={({target}) => this.setCustomerState({caretaker_name_1: target.value})}
											style={{marginBottom: "10px"}}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>

										<TextField
											name="caretaker_ssn_1"
											label="Personnr"
											autocomplete="off"
											value={customerState.caretaker_ssn_1}
											validation={this._ssnValidation}
											onChange={({target}) => this.setCustomerState({caretaker_ssn_1: target.value})}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>

										<p style={{
											padding: "10px",
											lineHeight: "22px",
											background: "#ffffcc"
										}}>
											Intyg om ensam vårdnadshavare kommer behöva bifogas på sidan "Övriga
											dokument".
										</p>

										<input type="hidden" name="child_caretaker_intyg" value="yes"/>
									</div>
								</StackLayout>
							</React.Fragment>
						)}

						{customerState.needsGuardian && customerState.child_caretaker === "two" && (
							<React.Fragment>
								<StackLayout className="Cob-fields-collection" alignItems="start" fullWidth>
									<div>
										<h4>Vårdnadshavare 1</h4>

										<TextField
											name="caretaker_name_2"
											label="Namn"
											autocomplete="off"
											value={customerState.caretaker_name_2}
											validation={this._textValidation}
											onChange={({target}) => this.setCustomerState({caretaker_name_2: target.value})}
											style={{marginBottom: "10px"}}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>

										<TextField
											name="caretaker_ssn_2"
											label="Personnr"
											autocomplete="off"
											value={customerState.caretaker_ssn_2}
											validation={this._ssnValidation}
											onChange={({target}) => this.setCustomerState({caretaker_ssn_2: target.value})}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>
									</div>
									<div>
										<h4>Vårdnadshavare 2</h4>

										<TextField
											name="caretaker_name_3"
											label="Namn"
											autocomplete="off"
											value={customerState.caretaker_name_3}
											validation={this._textValidation}
											onChange={({target}) => this.setCustomerState({caretaker_name_3: target.value})}
											style={{marginBottom: "10px"}}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>

										<TextField
											name="caretaker_ssn_3"
											label="Personnr"
											autocomplete="off"
											value={customerState.caretaker_ssn_3}
											validation={this._ssnValidation}
											onChange={({target}) => this.setCustomerState({caretaker_ssn_3: target.value})}
											highlightErrors={hasFormValidationErrors}
											readOnly={readOnly}
										/>
									</div>
								</StackLayout>
							</React.Fragment>
						)}

						<div className="h-line spacing"/>

						<h3>Skattehemvist</h3>

						<StackLayout className="Cob-fields-collection per-row-3" rowWrap fullWidth>

							<div>
								<Select
									name="customer_citizenship"
									label={i18n("general", "citizenship")}
									selectedIndex={customerState.citizenshipIndex}
									options={this.countries}
									onChange={({value: citizenship}, citizenshipIndex) => this.setCustomerState({
										citizenship,
										citizenshipIndex
									})}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
							</div>

							<div>
								<Select
									name="customer_tax_country"
									label={i18n("general", "tax_country")}
									selectedIndex={customerState.taxCountryIndex}
									options={this.countries}
									onChange={({value: taxCountry}, taxCountryIndex) => this.setCustomerState({
										taxCountry,
										taxCountryIndex
									})}
									highlightErrors={hasFormValidationErrors}
									readOnly={readOnly}
								/>
							</div>

							{customerState.taxCountry !== 'Sverige' && (
								<div>
									<TextField
										name="customer_TIN"
										value={customerState.customer_TIN}
										autocomplete="off"
										label="TIN nr"
										validation={value => value.length > 0}
										onChange={({target}) => this.setCustomerState({customer_TIN: target.value})}
										highlightErrors={hasFormValidationErrors}
										readOnly={readOnly}
									/>
									<input type="hidden" name="radio_customer_kyc_taxdom" value="other"/>
								</div>
							)}

							{customerState.taxCountry === 'Sverige' && (
								<React.Fragment>
									<input type="hidden" name="radio_customer_kyc_taxdom" value="swe"/>
									<input type="hidden" name="customer_tax_country" value=""/>
									<input type="hidden" name="customer_TIN" value=""/>
								</React.Fragment>
							)}
						</StackLayout>

						<div className="h-line spacing"/>
						<h4 style={{width: "100%", padding: "0 0 5px 0"}}>
							Bakgrunden/anledningen till varför kunden söker portföljförvaltning/investeringsrådgivning vid detta tillfälle.
						</h4>
						<p style={{margin: "-10px 0 10px 0"}}>
							Vad har kunden för lösning idag och målsättning idag? Finns det någon del vi kan hjälpa
							till med utifrån kundens målsättning?
						</p>

						<TextView
							name="advice_notes"
							label={`Fritext (${1800 - customerState.advice_notes.length} tecken kvar)`}
							value={customerState.advice_notes}
							maxLength={1800}
							validation={value => value.length > 0}
							resize="vertical"
							onChange={({target}) => this.setCustomerState({advice_notes: target.value})}
							fullWidth
							minHeight={250}
							highlightErrors={hasFormValidationErrors}
							readOnly={readOnly}
						/>

						<StackLayout style={{marginTop: "30px"}}>

							<OptionsList
								type="checkbox"
								description="Vad är kundens mål med investeringen?"
								orientation="vertical"
								style={{
									paddingTop: "20px",
									width: "400px"
								}}
								value={
									customerState.checkbox_customer_kyc_purpose_pension === "Ja" ||
									customerState.checkbox_customer_kyc_purpose_beneficiary === "Ja" ||
									customerState.checkbox_customer_kyc_purpose_speculation === "Ja" ||
									customerState.checkbox_customer_kyc_purpose_regpayments === "Ja" ||
									customerState.checkbox_customer_kyc_purpose_financial_growth === "Ja" ||
									customerState.checkbox_customer_kyc_purpose_other === "Ja" ?
										"filled" :
										""
								}
								options={[
									{
										name: "checkbox_customer_kyc_purpose_pension",
										label: "Öka pension",
										value: customerState.checkbox_customer_kyc_purpose_pension === "Ja" ? "Ja" : "Nej",
										checked: customerState.checkbox_customer_kyc_purpose_pension === "Ja",
										onChange: checked => this.setCustomerState({checkbox_customer_kyc_purpose_pension: checked ? "Ja" : "Nej"})
									},
									{
										name: "checkbox_customer_kyc_purpose_beneficiary",
										label: "Trygga efterlevande",
										value: customerState.checkbox_customer_kyc_purpose_beneficiary === "Ja" ? "Ja" : "Nej",
										checked: customerState.checkbox_customer_kyc_purpose_beneficiary === "Ja",
										onChange: checked => this.setCustomerState({checkbox_customer_kyc_purpose_beneficiary: checked ? "Ja" : "Nej"})
									},
									{
										name: "checkbox_customer_kyc_purpose_speculation",
										label: "Spekulation",
										value: customerState.checkbox_customer_kyc_purpose_speculation === "Ja" ? "Ja" : "Nej",
										checked: customerState.checkbox_customer_kyc_purpose_speculation === "Ja",
										onChange: checked => this.setCustomerState({checkbox_customer_kyc_purpose_speculation: checked ? "Ja" : "Nej"})
									},
									{
										name: "checkbox_customer_kyc_purpose_regpayments",
										label: "Regelbunden utbetalning",
										value: customerState.checkbox_customer_kyc_purpose_regpayments === "Ja" ? "Ja" : "Nej",
										checked: customerState.checkbox_customer_kyc_purpose_regpayments === "Ja",
										onChange: checked => this.setCustomerState({checkbox_customer_kyc_purpose_regpayments: checked ? "Ja" : "Nej"})
									},
									{
										name: "checkbox_customer_kyc_purpose_financial_growth",
										label: "Ekonomisk tillväxt (långsiktig)",
										value: customerState.checkbox_customer_kyc_purpose_financial_growth === "Ja" ? "Ja" : "Nej",
										checked: customerState.checkbox_customer_kyc_purpose_financial_growth === "Ja",
										onChange: checked => this.setCustomerState({checkbox_customer_kyc_purpose_financial_growth: checked ? "Ja" : "Nej"})
									},
									{
										name: "checkbox_customer_kyc_purpose_other",
										label: "Annat",
										value: customerState.checkbox_customer_kyc_purpose_other === "Ja" ? "Ja" : "Nej",
										checked: customerState.checkbox_customer_kyc_purpose_other === "Ja",
										onChange: checked => this.setCustomerState({checkbox_customer_kyc_purpose_other: checked ? "Ja" : "Nej"})
									}
								]}
								validation={value => (value && value === "filled") || false}
								highlightErrors={hasFormValidationErrors}
								readOnly={readOnly}
							/>
						</StackLayout>
						{customerState.checkbox_customer_kyc_purpose_other === "Ja" && (<TextField
								name="customer_kyc_purpose_savinggoal"
								label="Vänligen beskriv"
								value={customerState.customer_kyc_purpose_savinggoal}
								validation={this._textValidation}
								style={{marginTop: "-10px", marginLeft: "11px"}}
								onChange={({target}) => this.setCustomerState({customer_kyc_purpose_savinggoal: target.value})}
								highlightErrors={hasFormValidationErrors}
								readOnly={readOnly}
							/>)}
					</div>
				</ContentBox>
			</div>
		);
	}

	_textValidation = (value) => (value.length > 0);

	_optionsValidation = (value) => ((value && value.length > 0) || false);

	/**
	 * SSN numbers need to be 12 digits and start with 19 or 20
	 */
	_ssnValidation = (value) => (!isNaN(value) && value.length === 12 &&
		(value.substring(0, 2) === "19" || value.substring(0, 2) === "20") && Validator.validateSsn(value)
	);

	_onLeiUpdated = (fulfilled) =>
	{
		this.setCustomerState({leiCodeValid: fulfilled});
	}
}

// PRIVATE FUNCTIONS
function _getInitialState()
{
	const {customer, viewNo, hasFormValidationErrors, readOnly} = this.props;
	const seenViews = JSON.parse(localStorage.getItem(`SEEN_VIEWS_${btoa(customer.caseId)}`)) || [];
	const hasBeenSeen = (seenViews.includes(viewNo));

	// if it hasn't been seen add the view to local storage for the specific case
	if (customer.caseId && !hasBeenSeen)
	{
		localStorage.setItem(`SEEN_VIEWS_${btoa(customer.caseId)}`, JSON.stringify([...seenViews, viewNo]));
	}

	let newState = getViewInitialStateForCustomer(customer);
	newState.isFulfilled = this.isFulfilled(newState);
	newState.hasFormValidationErrors = hasBeenSeen || hasFormValidationErrors;
  newState.readOnly = readOnly;
	return newState;
}

export function getViewInitialStateForCustomer(customer)
{
	const newState = {
		isFulfilled: false,
		isLoading: true,
		lookupNotFound: false,
		fraudFlag: false,
		errorMessage: null,
		customerState: {
			isCompany: customer.isCompany,

			date: customer.getData("date") || "",
			meetingFormat: customer.getData("radio_meeting_type", null),

			meetingFormatIndex: (
				customer.getData("radio_meeting_type") === "personal" ? 0 :
					customer.getData("radio_meeting_type") === "phone" ? 1 :
						customer.getData("radio_meeting_type") === "video" ? 2 :
							-1),

			checkbox_idcheck_id: customer.getData("checkbox_idcheck_id") ? "yes" : "no",
			checkbox_idcheck_drivinglic: customer.getData("checkbox_idcheck_drivinglic") ? "yes" : "no",
			checkbox_idcheck_passport: customer.getData("checkbox_idcheck_passport") ? "yes" : "no",
			checkbox_idcheck_known: customer.getData("checkbox_idcheck_known") ? "Ja" : "no",

			checkbox_customer_extcheck: customer.getData("checkbox_customer_extcheck", ""),
			checkbox_customer_countercheck: customer.getData("checkbox_customer_countercheck", ""),

			ssnOrg: customer.getData("customer_social_security_number"),
			firstName: customer.getData("customer_firstname"),
			lastName: customer.getData("customer_lastname"),
			street: customer.getData("customer_address_street"),
			zipCode: customer.getData("customer_address_zipcode"),
			city: customer.getData("customer_address_city"),
			country: customer.getData("customer_address_country") || "Sverige",
			countryIndex: countries.map(c => c.label).indexOf(customer.getData("customer_address_country") || "Sverige"),
			citizenship: customer.getData("customer_citizenship") || "Sverige",
			citizenshipIndex: countries.map(c => c.label).indexOf(customer.getData("customer_citizenship") || "Sverige"),
			taxCountry: customer.getData("customer_tax_country") || "Sverige",
			customer_TIN: customer.getData("customer_TIN") || "",
			taxCountryIndex: countries.map(c => c.label).indexOf(customer.getData("customer_tax_country") || "Sverige"),
			phoneNumber: customer.getData("customer_phonenumber_cell"),
			phoneNumberHome: customer.getData("customer_phonenumber_home"),
			email: customer.getData("customer_email"),

			child_caretaker: customer.getData("child_caretaker"),

			leiCodeValid: LeiCodeFieldFulfillment.call(this, LeiCodeField.getViewInitialStateForCustomer(customer, null, false), null),

			companyReps: customer.companyRepsFair,
			companyRepsActiveAmount: 0,
			companySignatories: 1,
			isForeigner: false,
			isNonEuPerson: false,

			checkbox_customer_kyc_inv_own_part: customer.getData("checkbox_customer_kyc_inv_own_part") || "no",
			checkbox_customer_kyc_inv_through_poa: customer.isCompany ? "ja" : customer.getData("checkbox_customer_kyc_inv_through_poa") || "no",

			caretaker_name_1: customer.getData("caretaker_name_1"),
			caretaker_ssn_1: customer.getData("caretaker_ssn_1"),
			caretaker_name_2: customer.getData("caretaker_name_2"),
			caretaker_ssn_2: customer.getData("caretaker_ssn_2"),
			caretaker_name_3: customer.getData("caretaker_name_3"),
			caretaker_ssn_3: customer.getData("caretaker_ssn_3"),

			customer_inv_poa_firstname: customer.getData("customer_inv_poa_firstname"),
			customer_inv_poa_lastname: customer.getData("customer_inv_poa_lastname"),
			customer_inv_poa_ssn: customer.getData("customer_inv_poa_ssn"),

			customer_inv_poa_firstname_2: customer.getData("customer_inv_poa_firstname_2"),
			customer_inv_poa_lastname_2: customer.getData("customer_inv_poa_lastname_2"),
			customer_inv_poa_ssn_2: customer.getData("customer_inv_poa_ssn_2"),

			customer_inv_poa_firstname_3: customer.getData("customer_inv_poa_firstname_3"),
			customer_inv_poa_lastname_3: customer.getData("customer_inv_poa_lastname_3"),
			customer_inv_poa_ssn_3: customer.getData("customer_inv_poa_ssn_3"),

			customer_inv_poa_firstname_4: customer.getData("customer_inv_poa_firstname_4"),
			customer_inv_poa_lastname_4: customer.getData("customer_inv_poa_lastname_4"),
			customer_inv_poa_ssn_4: customer.getData("customer_inv_poa_ssn_4"),

			advice_notes: customer.getData("advice_notes") || "",

			checkbox_customer_kyc_purpose_pension: customer.getData("checkbox_customer_kyc_purpose_pension", null),
			checkbox_customer_kyc_purpose_beneficiary: customer.getData("checkbox_customer_kyc_purpose_beneficiary", null),
			checkbox_customer_kyc_purpose_speculation: customer.getData("checkbox_customer_kyc_purpose_speculation", null),
			checkbox_customer_kyc_purpose_regpayments: customer.getData("checkbox_customer_kyc_purpose_regpayments", null),
			checkbox_customer_kyc_purpose_financial_growth: customer.getData("checkbox_customer_kyc_purpose_financial_growth", null),
			checkbox_customer_kyc_purpose_other: customer.getData("checkbox_customer_kyc_purpose_other", null),
			customer_kyc_purpose_savinggoal: customer.getData("customer_kyc_purpose_savinggoal") || "",
		}
	};

	for (let i = 0; i < 4; i++)
	{
		const rep = newState.customerState.companyReps[i];
		if (rep.firstName)
		{
			newState.customerState.companyRepsActiveAmount++;
		}
		else
		{
			break;
		}
	}

	// Check if the person is a minor. If so, we will need to enter their guardians.
	let needsGuardianToSign = false;
	if (newState.customerState.ssnOrg.startsWith("20"))
	{
		const dateOfBirthYear = newState.customerState.ssnOrg.substring(0, 4);
		const dateOfBirthMonth = newState.customerState.ssnOrg.substring(4, 6);
		const dateOfBirthDay = newState.customerState.ssnOrg.substring(6, 8);
		const dateOfBirth = new Date(dateOfBirthYear + "-" + dateOfBirthMonth + "-" + dateOfBirthDay);

		const date18yearsAgo = new Date();
		date18yearsAgo.setFullYear(date18yearsAgo.getFullYear() - 18);

		if (dateOfBirth > date18yearsAgo)
		{
			needsGuardianToSign = true;
		}
	}

	newState.customerState.needsGuardian = !customer.isCompany && needsGuardianToSign;

	newState.customerState.companySignatories =
		newState.customerState.customer_inv_poa_ssn_4.length > 0 ? 4 :
			newState.customerState.customer_inv_poa_ssn_3.length > 0 ? 3 :
				newState.customerState.customer_inv_poa_ssn_2.length > 0 ? 2 : 1;

	if (newState.customerState.date === "")
	{
		const today = new Date();
		let dayDate = today.getDate() < 10 ? "0" + today.getDate() : today.getDate();
		let monthDate = (today.getMonth() + 1) < 10 ? "0" + (today.getMonth() + 1) : (today.getMonth() + 1);
		newState.customerState.date = today.getFullYear() + '-' + monthDate + '-' + dayDate;
	}

	if (newState.customerState.firstName === "" &&
		customer._clientData &&
		customer._clientData.first_name &&
		customer._clientData.first_name.length > 0)
	{
		newState.customerState.firstName = customer._clientData.first_name;
	}

	if (newState.customerState.firstName === "" &&
		customer._clientData &&
		customer._clientData.name &&
		customer._clientData.name.length > 0)
	{
		newState.customerState.firstName = customer._clientData.name;
	}

	if (newState.customerState.lastName === "" &&
		customer._clientData &&
		customer._clientData.last_name &&
		customer._clientData.last_name.length > 0)
	{
		newState.customerState.lastName = customer._clientData.last_name;
	}

	if (newState.customerState.email === "" &&
		customer._clientData &&
		customer._clientData.email &&
		customer._clientData.email.length > 0)
	{
		newState.customerState.email = customer._clientData.email;
	}

	if (newState.customerState.phoneNumber === "" &&
		customer._clientData &&
		customer._clientData.phone &&
		customer._clientData.phone.length > 0)
	{
		newState.customerState.phoneNumber = customer._clientData.phone;
	}

	if (newState.customerState.phoneNumberHome === "" &&
		customer._clientData &&
		customer._clientData.phone_alternative &&
		customer._clientData.phone_alternative.length > 0)
	{
		newState.customerState.phoneNumberHome = customer._clientData.phone_alternative;
	}

	if (newState.customerState.zipCode === "" &&
		customer._clientData &&
		customer._clientData.postal_code &&
		customer._clientData.postal_code.length > 0)
	{
		newState.customerState.zipCode = customer._clientData.postal_code;
	}

	if (newState.customerState.street === "" &&
		customer._clientData &&
		customer._clientData.street &&
		customer._clientData.street.length > 0)
	{
		newState.customerState.street = customer._clientData.street;
	}

	if (newState.customerState.city === "" &&
		customer._clientData &&
		customer._clientData.city &&
		customer._clientData.city.length > 0)
	{
		newState.customerState.city = customer._clientData.city;
	}

	return newState;
}

function _getFields()
{
	let fields = [
		"company_city_hq",
		"customer_social_security_number",
		"customer_firstname",
		"customer_lastname",
		"customer_address_street",
		"customer_address_zipcode",
		"customer_address_city",
		"customer_address_country",
		"customer_citizenship",
		"customer_tax_country",
		"customer_TIN",
		"customer_phonenumber_home",
		"customer_phonenumber_cell",
		"customer_email",

		"checkbox_customer_kyc_inv_own_part",
		"checkbox_customer_kyc_inv_through_poa",
		"customer_inv_poa_firstname",
		"customer_inv_poa_lastname",
		"customer_inv_poa_ssn",
		"customer_inv_poa_firstname_2",
		"customer_inv_poa_lastname_2",
		"customer_inv_poa_ssn_2",
		"customer_inv_poa_firstname_3",
		"customer_inv_poa_lastname_3",
		"customer_inv_poa_ssn_3",
		"customer_inv_poa_firstname_4",
		"customer_inv_poa_lastname_4",
		"customer_inv_poa_ssn_4",
		"child_caretaker",

		"caretaker_name_1",
		"caretaker_ssn_1",
		"child_caretaker_intyg",

		"caretaker_name_2",
		"caretaker_ssn_2",

		"caretaker_name_3",
		"caretaker_ssn_3",

		"advice_notes",

		"checkbox_customer_kyc_purpose_pension",
		"checkbox_customer_kyc_purpose_beneficiary",
		"checkbox_customer_kyc_purpose_speculation",
		"checkbox_customer_kyc_purpose_regpayments",
		"checkbox_customer_kyc_purpose_financial_growth",
		"checkbox_customer_kyc_purpose_other",
		"customer_kyc_purpose_savinggoal",

		"date",
		"radio_meeting_type",
	];

	fields = fields.concat(LeiCodeField.getFields());
	return fields;
}